import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColumnKey } from '../../models';
import { SELECTOR_STANDARD_GROUPS, SELECTOR_BUDGETING_GROUPS, PRO_MODE_COLUMNS } from '../../constants';
import { formatColumnLabel, REQUIRED_COLUMN_KEYS } from '../../utils/bill-utils';

interface ColumnItem {
    key: ColumnKey;
    label: string;
    required: boolean;
}

interface ColumnGroupView {
    title: string;
    columns: ColumnItem[];
}

@Component({
  selector: 'app-column-selector',
  standalone: true,
  imports: [MatCheckboxModule, MatTooltipModule],
  template: `
    <div class="column-selector" [style.top]="positionStyle.top" [style.right]="positionStyle.right" [style.max-height]="positionStyle.maxHeight">
      <div class="header">
        <span>Table Columns</span>
        <div class="header-actions">
          <button (click)="resetColumns.emit()" class="reset-btn">Reset</button>
          <button (click)="close.emit()" class="done-btn">Done</button>
        </div>
      </div>
      <div class="body">
        <!-- Left panel — Standard columns -->
        <div class="panel panel-standard">
          <div class="panel-header">
            <i class="material-symbols-rounded">view_column</i>
            <span>Standard</span>
          </div>
          @for (group of standardGroups; track group.title) {
            <div class="column-group">
              <div class="group-header">
                <span class="group-title">{{ group.title }}</span>
                <div class="group-divider"></div>
              </div>
              <div class="column-list">
                @for (col of group.columns; track col.key) {
                  <label class="column-item"
                    [class.required]="col.required"
                    [matTooltip]="col.required ? 'This column is required and cannot be hidden' : ''"
                    matTooltipPosition="left">
                    <mat-checkbox
                      [checked]="visibleColumns[col.key]"
                      [disabled]="col.required"
                      (change)="toggle(col.key)"
                    ></mat-checkbox>
                    <span class="label-text">{{ col.label }}</span>
                  </label>
                }
              </div>
            </div>
          }
        </div>

        <!-- Right panel — Budgeting columns -->
        <div class="panel panel-budgeting" [class.disabled]="!isBudgetingMode">
          <div class="panel-header budgeting-header">
            <i class="material-symbols-rounded">analytics</i>
            <span>Budgeting</span>
          </div>
          @for (group of budgetingGroups; track group.title) {
            <div class="column-group">
              <div class="group-header">
                <span class="group-title">{{ group.title }}</span>
                <div class="group-divider"></div>
              </div>
              <div class="column-list">
                @for (col of group.columns; track col.key) {
                  <label class="column-item" [class.disabled]="!isBudgetingMode">
                    <mat-checkbox
                      [checked]="visibleColumns[col.key]"
                      (change)="toggle(col.key)"
                      [disabled]="!isBudgetingMode"
                    ></mat-checkbox>
                    <span class="label-text" [class.muted]="!isBudgetingMode">{{ col.label }}</span>
                  </label>
                }
              </div>
            </div>
          }
          @if (!hasBudgetingFeature) {
            <div class="budgeting-cta">
              <i class="material-symbols-rounded">lock</i>
              <span>Ask your account manager about budgeting mode</span>
            </div>
          } @else if (!isBudgetingMode) {
            <div class="budgeting-cta">
              <i class="material-symbols-rounded">toggle_on</i>
              <span>Turn on Budgeting Mode to use these columns</span>
            </div>
          }
        </div>
      </div>
    </div>
    `,
  styleUrl: './column-selector.component.scss'
})
export class ColumnSelectorComponent implements OnInit {
    @Input() visibleColumns!: Record<ColumnKey, boolean>;
    @Input() isBudgetingMode = false;
    /** Org-level budgeting entitlement — gates the upsell so entitled orgs (toggle off) aren't upsold. */
    @Input() hasBudgetingFeature = false;
    @Input() positionStyle: { top: string; right: string; maxHeight: string } = { top: '0', right: '0', maxHeight: '80vh' };
    @Output() toggleColumn = new EventEmitter<ColumnKey>();
    @Output() resetColumns = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();

    /** Pre-computed column groups (avoids method calls in template) */
    standardGroups: ColumnGroupView[] = [];
    budgetingGroups: ColumnGroupView[] = [];

    ngOnInit() {
        const mapGroup = (group: { title: string; columns: ColumnKey[] }): ColumnGroupView => ({
            title: group.title,
            columns: group.columns.map(key => ({
                key,
                label: formatColumnLabel(key),
                required: REQUIRED_COLUMN_KEYS.has(key)
            }))
        });

        this.standardGroups = SELECTOR_STANDARD_GROUPS.map(mapGroup);
        this.budgetingGroups = SELECTOR_BUDGETING_GROUPS.map(mapGroup);
    }

    toggle(key: ColumnKey) {
        if (REQUIRED_COLUMN_KEYS.has(key)) return;
        if (PRO_MODE_COLUMNS.has(key) && !this.isBudgetingMode) return;
        this.toggleColumn.emit(key);
    }
}
